<template>
  <BaseDrawer
    shrink
    :is-open="isOpen && !showReceiptDrawer"
    @close="cancel"
  >
    <DrawerHeader @close="cancel">
      Unlinked Refund
    </DrawerHeader>
    <div class="w-full space-y-2">
      <div class="space-y-2 px-6">
        <div class="space-y-2 px-8">
          <CustomAmountDisplay :value="amountToRefund" />

          <StatRow
            label="Maximum Refund"
            type="currency"
            :value="unlinkedRefundsSingleMaximum"
            large
          />
        </div>
        <BaseNumpad v-model="amountToRefund" />
      </div>
      <div class="p-4">
        <ActionButtonPrimary
          v-if="!cardholderNotPresentEnabled"
          class="w-full"
          :disabled="amountToRefund <= 0"
          @click="handleClickSubmit(false)"
        >
          Refund
        </ActionButtonPrimary>
        <template v-else>
          <ActionButtonPrimary
            class="w-full"
            :disabled="amountToRefund <= 0"
            @click="handleClickSubmit(false)"
          >
            Refund (Card)
          </ActionButtonPrimary>
          <ActionButtonSecondary
            class="w-full"
            :disabled="amountToRefund <= 0"
            @click="handleClickSubmit(true)"
          >
            Refund (CNP)
          </ActionButtonSecondary>
        </template>
      </div>
    </div>
  </BaseDrawer>

  <TransactionDrawer
    v-if="receiptTransaction"
    :is-open="showReceiptDrawer"
    :transaction="receiptTransaction"
    @close="handleCloseReceiptDrawer"
  />
</template>

<script setup lang="ts">
import { IPayment } from "@/androidPlugins/IPayment"
import { checkUnlinkedRefund } from "@/services/unlinked-refund/checkUnlinkedRefund"
import { createUnlinkedRefund } from "@/services/unlinked-refund/createUnlinkedRefund"
import { useConfirmDialog } from "@vueuse/core"

import { retrieveTransactionReceipt } from "@/services/transactions/retrieveTransactionReceipt"
import { IPrinter } from "@/androidPlugins/IPrinter"
import * as Sentry from "@sentry/vue"
import type { CreateRefundResponse } from "@/services/createRefund"

const {
  reveal,
  isRevealed: isOpen,
  onReveal,
  confirm,
  cancel,
} = useConfirmDialog()
const { unlinkedRefundsSingleMaximum, tenant } = useTenant()
const { startLoading, stopLoading } =
  usePageBlockingLoading()
const { cardholderNotPresentEnabled } =
  useCardholderNotPresent()
const { printingSupported } = useDevice()

const amountToRefund = ref(0)
const showReceiptDrawer = ref(false)
const receiptTransaction = ref<CreateRefundResponse>()

onReveal(() => {
  amountToRefund.value = 0
  showReceiptDrawer.value = false
  receiptTransaction.value = undefined
})

const handleClickSubmit = async (
  cardholderNotPresent: boolean
) => {
  if (amountToRefund.value <= 0) return

  startLoading()

  if (
    amountToRefund.value >
    unlinkedRefundsSingleMaximum.value
  ) {
    toast.error("Refund amount exceeds maximum refund")
  } else {
    const response = await checkUnlinkedRefund({
      amount: amountToRefund.value,
    })
    if (!response.body.allowed) {
      toast.error(response.body.error)
    } else {
      try {
        const deviceResponse =
          await IPayment.makeDeviceRefund({
            amountTrans: amountToRefund.value,
            disablePrinting:
              !tenant.isCardReceiptsEnabled ||
              !printingSupported.value,
            retrievalReferenceNumber: null,
            cardholderNotPresent,
          })

        try {
          const refundResponse = await createUnlinkedRefund(
            {
              source: "pos_card",
              data: deviceResponse.data,
            }
          )
          if (refundResponse.status === "successful") {
            await onSuccess(refundResponse)
          } else {
            toast.error("Refund failed")
          }
        } catch {
          // Error in create_refund api call
          toast.info("Error logging refund result", 10000)
          cancel()
        }
      } catch (error) {
        // Likely error handing over to payment application
        toast.error("Failed to refund")
        Sentry.captureException(error)
      }
    }
  }

  stopLoading()
}

const onSuccess = async (refund: CreateRefundResponse) => {
  receiptTransaction.value = refund
  if (
    tenant.isAutomaticMerchantReceiptEnabled &&
    printingSupported.value
  ) {
    startLoading()
    try {
      const { receipt } = await retrieveTransactionReceipt(
        refund.id,
        {
          isMerchant: true,
        }
      )

      await IPrinter.printStr({ str: receipt })
    } catch {
      toast.error("Failed to generate receipt")
    } finally {
      stopLoading()
    }
  }

  if (tenant.isTransactionReceiptsEnabled) {
    showReceiptDrawer.value = true
  } else {
    toast.success("Refund successful")
    confirm()
  }
}

const handleCloseReceiptDrawer = () => {
  showReceiptDrawer.value = false
  confirm()
}

defineExpose({
  reveal,
})
</script>
